import type { UserReferral } from "@decibeltrade/sdk";
import { useTranslations } from "next-intl";
import { useMemo } from "react";
import { css } from "styled-system/css";
import { flex, stack } from "styled-system/patterns";

import { DefaultError } from "@/components/handle-query";
import { Skeleton } from "@/components/skeleton";
import { useUserReferralsQuery } from "@/contexts/queries/user-referrals";
import { getServerTime, useServerTimeDelta } from "@/utils/server-time";

import { SectionCard } from "../../components";

const DAY_MS = 24 * 60 * 60 * 1000;
const WINDOW_DAYS = 14;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

interface DayBucket {
  /** UTC calendar day, `YYYY-MM-DD`. */
  iso: string;
  label: string;
  signUps: number;
}

function toUtcDay(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

function formatDayLabel(iso: string): string {
  const [, month, day] = iso.split("-");
  return `${MONTHS[Number(month) - 1] ?? ""} ${Number(day)}`;
}

/**
 * Buckets referrals into the last `WINDOW_DAYS` UTC days.
 *
 * Every day in the window is emitted, including empty ones: grouping only the
 * days that have referrals would compress the axis and misrepresent the trend.
 */
function bucketByDay(referrals: UserReferral[], nowMs: number): DayBucket[] {
  const counts = new Map<string, number>();
  for (const referral of referrals) {
    const iso = toUtcDay(referral.referred_at_ms);
    counts.set(iso, (counts.get(iso) ?? 0) + 1);
  }

  return Array.from({ length: WINDOW_DAYS }, (_, index) => {
    const iso = toUtcDay(nowMs - (WINDOW_DAYS - 1 - index) * DAY_MS);
    return { iso, label: formatDayLabel(iso), signUps: counts.get(iso) ?? 0 };
  });
}

export const SignUpsChart = ({ account }: { account: string | undefined }) => {
  const t = useTranslations("affiliates.overview.signUpsChart");
  const query = useUserReferralsQuery(account);

  // getServerTime() decides which UTC days the window covers, so the chart must
  // re-render once the real clock delta arrives.
  useServerTimeDelta();
  const nowMs = getServerTime();

  const referrals = query.data;
  const days = useMemo(() => (referrals ? bucketByDay(referrals, nowMs) : []), [referrals, nowMs]);

  if (query.isError) {
    return (
      <SectionCard label={t("window")} title={t("title")}>
        <DefaultError error={query.error} resetErrorBoundary={() => void query.refetch()} />
      </SectionCard>
    );
  }

  if (referrals === undefined) {
    return (
      <SectionCard label={t("window")} title={t("title")}>
        <Skeleton className={css({ w: "full", h: "[120px]", rounded: "200" })} />
      </SectionCard>
    );
  }

  const total = days.reduce((sum, day) => sum + day.signUps, 0);
  // Floor the divisor at 1 so an all-zero window renders flat instead of NaN.
  const max = Math.max(...days.map((day) => day.signUps), 1);

  return (
    <SectionCard
      label={t("window")}
      title={t("title")}
      rightSlot={
        <span className={css({ textStyle: "body.md", color: "text.tertiary" })}>
          {t("total", { count: total.toLocaleString("en-US") })}
        </span>
      }
    >
      <div className={stack({ gap: "8" })}>
        <div className={chartAreaStyles}>
          {days.map((day) => (
            <div
              key={day.iso}
              className={barCellStyles}
              title={t("barTooltip", { label: day.label, count: day.signUps })}
            >
              <div
                className={css({ w: "full", bg: "lilac.100", roundedTop: "200" })}
                style={{ height: `${(day.signUps / max) * 100}%` }}
              />
            </div>
          ))}
        </div>
        <div className={flex({ justifyContent: "space-between" })}>
          <span className={axisLabelStyles}>{days[0]?.label}</span>
          <span className={axisLabelStyles}>{days[days.length - 1]?.label}</span>
        </div>
      </div>
    </SectionCard>
  );
};

const chartAreaStyles = flex({
  alignItems: "flex-end",
  gap: "4",
  h: "[120px]",
  w: "full",
});

const barCellStyles = css({
  flex: "1",
  display: "flex",
  alignItems: "flex-end",
  minW: "0",
  h: "full",
});

const axisLabelStyles = css({
  textStyle: "body.sm",
  color: "text.tertiary",
});
